import { Download, ExternalLink } from 'lucide-react'
import { ShotSelectLogo } from '../components/ShotSelectLogo'

/**
 * A browser-safe handoff for a cloud-project invitation.
 *
 * This deliberately does not accept an invite: the cloud project lives in the
 * native app, where the recipient signs in and the project cache is hydrated.
 * Keeping that boundary visible prevents a phone/browser visitor from
 * believing they have already joined a shared shoot.
 */
export function InviteHandoff({ token }: { token: string }) {
  const nativeInviteUrl = `shotselect://invite/${token}`

  return (
    <main
      data-testid="invite-handoff"
      className="min-h-[100dvh] px-6 py-8 flex items-center justify-center text-center"
    >
      <section className="w-full max-w-sm flex flex-col items-center">
        <ShotSelectLogo className="h-12 w-12" title="ShotSelect" />
        <p className="mt-7 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--text-tertiary)]">
          Shared project
        </p>
        <h1 className="mt-3 text-3xl font-semibold tracking-[-0.03em] text-[var(--text-primary)]">
          Open this project in ShotSelect
        </h1>
        <p className="mt-3 max-w-xs text-sm leading-6 text-[var(--text-secondary)]">
          Shared projects open in the ShotSelect desktop app. Sign in there with the email address
          that received this invite.
        </p>

        <a
          data-testid="invite-handoff-open-app"
          href={nativeInviteUrl}
          className="mt-7 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-[var(--accent-primary)] px-4 py-2.5 text-sm font-semibold text-[#0a0a0a]"
        >
          <ExternalLink size={16} strokeWidth={2.25} />
          Open ShotSelect
        </a>

        <p className="mt-5 max-w-xs text-[13px] leading-5 text-[var(--text-tertiary)]">
          Viewing this page does not accept the invite or reveal the project in your browser.
        </p>

        <a
          data-testid="invite-handoff-download"
          href="https://shotselect.app/#download"
          className="mt-5 inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-[var(--border-default)] px-4 py-2.5 text-sm font-medium text-[var(--text-primary)]"
        >
          <Download size={16} strokeWidth={2.25} />
          Download ShotSelect
        </a>

        <p className="mt-4 max-w-xs text-[12px] leading-5 text-[var(--text-tertiary)]">
          On a phone, keep this link and open it from your ShotSelect desktop app.
        </p>
      </section>
    </main>
  )
}
